import { Controller, useFormContext } from 'react-hook-form';
import { FORM_FIELDS, LABELS, MASKS, PLACEHOLDERS } from '../../../../../constants';
import { DateInput, onlyNumbersOutput, onlyRussianOutput, TextInput } from '../../../../landing.common';
import React, { FC, useCallback } from 'react';
import { FullNameOption, FullNameSuggest } from '../../../ui/suggest/full_name';
import { FocusedFieldProps } from '../../types';
import { extendFormData, updatePassportStepOptions } from '../../../../../store/reducers/formStore';
import { useDispatch } from 'react-redux';
import { IUnitCodeSuggest, UnitCodeSuggest } from '../../../ui/suggest/unit.code';
import { IssuedSuggest } from '../../../ui/suggest/issued';
import { usePassportStepOptions } from '../../../../../store/selectors/formStore';

export const BirthdayField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.BIRTHDAY}
            render={({ field }) => (
                <DateInput
                    {...field}
                    label="Дата рождения"
                    errorMessage={errors[FORM_FIELDS.BIRTHDAY]?.message}
                    {...props}
                />
            )}
        />
    );
};

export const FullNameField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const dispatch = useDispatch();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const onFullNameSelect = useCallback(
        async (option: FullNameOption) => {
            dispatch(
                extendFormData({
                    [FORM_FIELDS.FIRST_NAME]: option.firstName || '',
                    [FORM_FIELDS.LAST_NAME]: option.lastName || '',
                    [FORM_FIELDS.MIDDLE_NAME]: option.patronymic || '',
                    [FORM_FIELDS.GENDER]: option.gender || 'male',
                }),
            );
        },
        [dispatch],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.FULL_NAME}
            render={({ field: { onBlur, ...field } }) => (
                <FullNameSuggest
                    input={{
                        ...field,
                        label: LABELS[FORM_FIELDS.FULL_NAME],
                        errorMessage: errors[FORM_FIELDS.FULL_NAME]?.message,
                        ...props,
                    }}
                    onChange={field.onChange}
                    onSelect={onFullNameSelect}
                    onBlur={onBlur}
                />
            )}
        />
    );
};

export const BirthPlaceField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.BIRTH_PLACE}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label="Место рождения"
                    errorMessage={errors[FORM_FIELDS.BIRTH_PLACE]?.message}
                    onChange={onlyRussianOutput(field)}
                    {...props}
                />
            )}
        />
    );
};

export const PassportNumberField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.PASSPORT_NUMBER}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label={LABELS[FORM_FIELDS.PASSPORT_NUMBER]}
                    inputMode="numeric"
                    mask={MASKS.PASSPORT}
                    placeholder={PLACEHOLDERS.PASSPORT}
                    errorMessage={errors[FORM_FIELDS.PASSPORT_NUMBER]?.message}
                    onChange={field.onChange}
                    {...props}
                />
            )}
        />
    );
};

export const PassportDateField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.PASSPORT_DATE}
            render={({ field }) => (
                <DateInput
                    {...field}
                    label={LABELS[FORM_FIELDS.PASSPORT_DATE]}
                    errorMessage={errors[FORM_FIELDS.PASSPORT_DATE]?.message}
                    {...props}
                />
            )}
        />
    );
};

export const UnitCodeField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const dispatch = useDispatch();
    const {
        setValue,
        trigger,
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const onUnitCodeBlur = useCallback(
        async (
            event: FE.FieldChangeEvent,
            onBlur: (event: FE.FieldChangeEvent) => void,
            option?: IUnitCodeSuggest,
            optionsForAllCodes?: IUnitCodeSuggest[],
        ) => {
            if (option && optionsForAllCodes) {
                const options = optionsForAllCodes.filter((item) => item.code === option.code);
                const mappedOptions = new Map(options.map((item) => [item.name, item.name]));
                dispatch(updatePassportStepOptions({ issuedBy: Array.from(mappedOptions.values()) }));
                setValue(FORM_FIELDS.ISSUED, options?.length === 1 ? options[0].name : '');
            } else if (!(await trigger(FORM_FIELDS.UNIT_CODE))) {
                dispatch(updatePassportStepOptions({ issuedBy: [] }));

                setValue(FORM_FIELDS.ISSUED, '');
            }

            onBlur(event);
        },
        [dispatch, setValue, trigger],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.UNIT_CODE}
            render={({ field: { onBlur, ...field } }) => (
                <UnitCodeSuggest
                    input={{
                        ...field,
                        label: LABELS[FORM_FIELDS.UNIT_CODE],
                        errorMessage: errors[FORM_FIELDS.UNIT_CODE]?.message,
                        mask: MASKS.UNIT_CODE,
                        ...props,
                    }}
                    onBlur={(event, option, allOptions) => onUnitCodeBlur(event, onBlur, option, allOptions)}
                    onChange={field.onChange}
                />
            )}
        />
    );
};

export const PassportIssuedByField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;
    const { issuedBy } = usePassportStepOptions();

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.ISSUED}
            render={({ field }) => (
                <IssuedSuggest
                    input={{
                        ...field,
                        label: LABELS[FORM_FIELDS.ISSUED],
                        errorMessage: errors[FORM_FIELDS.ISSUED]?.message,
                        ...props,
                    }}
                    options={issuedBy}
                    onChange={field.onChange}
                />
            )}
        />
    );
};
